import type { DataTableSort } from "@app/ui/shared/datatable";
import type { ScopeEnvironment } from "./scope-sidebar";

export const getValidControlsSortFields = (
  isAggregateScope: boolean,
  scopeEnvironments: ScopeEnvironment[],
): Set<string> => {
  const fields = new Set<string>(["name"]);
  if (isAggregateScope) {
    fields.add("cumulativeStatus");
  }
  for (const env of scopeEnvironments) {
    fields.add(`env-${env.id}`);
  }
  return fields;
};

export const isControlsSortValidForScope = (
  sortBy: string,
  isAggregateScope: boolean,
  scopeEnvironments: ScopeEnvironment[],
) =>
  getValidControlsSortFields(isAggregateScope, scopeEnvironments).has(sortBy);

export const resolveControlsSortFromSearchParams = (
  params: URLSearchParams,
  isAggregateScope: boolean,
  scopeEnvironments: ScopeEnvironment[],
): DataTableSort | undefined => {
  const sortBy = params.get("controlsSortBy");
  const sortDir = params.get("controlsSortDir");
  if (sortBy && sortDir) {
    if (
      !isControlsSortValidForScope(sortBy, isAggregateScope, scopeEnvironments)
    ) {
      return undefined;
    }
    return {
      field: sortBy,
      direction: sortDir === "desc" ? "desc" : "asc",
    };
  }

  const legacySort = params.get("sort");
  if (!legacySort) return undefined;

  const nameDir = params.get("nameDir") === "desc" ? "desc" : "asc";
  const statusDir = params.get("statusDir") === "desc" ? "desc" : "asc";

  if (legacySort === "name") {
    return { field: "name", direction: nameDir };
  }
  // Legacy URL-only sort (never exposed in the old UI). No compliance-type column in
  // the ag-grid table — fall back to name order rather than frameworksAsString sort.
  if (legacySort === "category" || legacySort === "complianceType") {
    return { field: "name", direction: "asc" };
  }

  const statusField = isAggregateScope
    ? "cumulativeStatus"
    : scopeEnvironments.length === 1
      ? `env-${scopeEnvironments[0].id}`
      : "name";
  return { field: statusField, direction: statusDir };
};

/**
 * Sort for DataTable initial state.
 * - object: valid sort from URL (or legacy params)
 * - null: stale/invalid controlsSortBy in URL — ignore URL and use defaultSort
 * - undefined: no sort in URL — use defaultSort
 */
export const resolveControlsTableInitialSort = (
  params: URLSearchParams,
  isAggregateScope: boolean,
  scopeEnvironments: ScopeEnvironment[],
): DataTableSort | null | undefined => {
  const resolved = resolveControlsSortFromSearchParams(
    params,
    isAggregateScope,
    scopeEnvironments,
  );
  if (resolved) return resolved;
  if (params.get("controlsSortBy")) return null;
  return undefined;
};
